import type { Bounds, Camera, ObjectId } from '@/types';
import type { SceneGraph } from '@/core/scene/sceneGraph';
import { centerCameraOn, centerCameraOnBounds, fitCameraToBounds, unionBounds } from '@/core/camera/cameraOps';
import { expand } from '@/core/geometry/bounds';
import { useCameraStore } from '@/store/cameraStore';
import { useSelectionStore } from '@/store/selectionStore';
import type { CameraAnimator } from './cameraAnimator';

/**
 * Navigation targets are computed with the pure camera ops; how the camera gets there
 * (jump or smooth transition) is the animator's concern. Every function accepts an
 * optional animator; without one it jumps, which keeps tests and shortcuts simple.
 */

const go = (target: Camera, animator?: CameraAnimator): void => {
  if (animator) animator.to(target);
  else useCameraStore.getState().setCamera(target);
};

/** World bounds of the given objects according to the scene graph, or null. */
export const boundsOfObjects = (scene: SceneGraph, ids: readonly ObjectId[]): Bounds | null => {
  const list: Bounds[] = [];
  for (const id of ids) {
    const b = scene.boundsOf(id);
    if (b) list.push(b);
  }
  return unionBounds(list);
};

const viewportSize = () => useCameraStore.getState().viewport.size;

/** Object zoom is capped so a single small note never becomes a wall of text. */
const ZOOM_TO_OBJECT_MAX = 1.5;

export const zoomToBounds = (bounds: Bounds, animator?: CameraAnimator, maxZoom = ZOOM_TO_OBJECT_MAX): void => {
  go(fitCameraToBounds(bounds, viewportSize(), { padding: 80, maxZoom }), animator);
};

export const zoomToObject = (scene: SceneGraph, id: ObjectId, animator?: CameraAnimator): void => {
  const b = scene.boundsOf(id);
  if (b) zoomToBounds(b, animator);
};

/** Frames the selection; with nothing selected, frames the content. */
export const zoomToSelection = (scene: SceneGraph, animator?: CameraAnimator): void => {
  const bounds = boundsOfObjects(scene, useSelectionStore.getState().selection.objectIds);
  if (bounds) zoomToBounds(bounds, animator);
  else zoomToContent(scene, animator);
};

/** Frames every interactive object; falls back to the origin at 100% when empty. */
export const zoomToContent = (scene: SceneGraph, animator?: CameraAnimator): void => {
  const bounds = unionBounds(
    scene
      .ids()
      .filter((id) => scene.isInteractive(id))
      .map((id) => scene.boundsOf(id))
      .filter((b): b is Bounds => !!b),
  );
  if (bounds) go(fitCameraToBounds(bounds, viewportSize(), { padding: 64, maxZoom: 1 }), animator);
  else go(centerCameraOn({ x: 0, y: 0 }, viewportSize(), 1), animator);
};

/** Overview: everything in the workspace (hidden and minimized included) plus the origin, never above 100%. */
export const zoomToWorkspace = (scene: SceneGraph, animator?: CameraAnimator): void => {
  const all = scene.contentBounds();
  const withOrigin = all
    ? {
        minX: Math.min(all.minX, 0),
        minY: Math.min(all.minY, 0),
        maxX: Math.max(all.maxX, 0),
        maxY: Math.max(all.maxY, 0),
      }
    : { minX: -400, minY: -300, maxX: 400, maxY: 300 };
  go(fitCameraToBounds(expand(withOrigin, 48), viewportSize(), { padding: 48, maxZoom: 1 }), animator);
};

export const centerOnObject = (scene: SceneGraph, id: ObjectId, animator?: CameraAnimator): void => {
  const b = scene.boundsOf(id);
  if (b) go(centerCameraOnBounds(useCameraStore.getState().camera, b, viewportSize()), animator);
};

/** Centres on the current selection; with nothing selected, fits all content. */
export const centerOnSelection = (scene: SceneGraph, animator?: CameraAnimator): void => {
  const bounds = boundsOfObjects(scene, useSelectionStore.getState().selection.objectIds);
  if (bounds) go(centerCameraOnBounds(useCameraStore.getState().camera, bounds, viewportSize()), animator);
  else zoomToContent(scene, animator);
};

/** Kept for callers that only need the jump form. */
export const fitToContent = (scene: SceneGraph): void => {
  zoomToContent(scene);
};

export const goToCamera = (camera: Camera, animator?: CameraAnimator): void => {
  go(camera, animator);
};
